import React, { useEffect, useState } from 'react';
import { UserCircle, Lock, Save } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { useAuth } from '@/contexts/AuthContext';
import { US_STATES } from '@/constants/usStates';
import StaffSmsConsentFields from '@/components/admin/StaffSmsConsentFields';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

const inputClass =
  "w-full bg-white/5 border border-white/10 text-white text-sm px-3 py-2.5 font-['Manrope'] focus:outline-none focus:border-blue-500/50";
const labelClass = "block text-xs font-['Manrope'] text-white/60 mb-1.5";

const emptyProfile = {
  first_name: '',
  last_name: '',
  email: '',
  phone: '',
  address_line1: '',
  address_line2: '',
  state: '',
  zip: '',
};

export default function AdminProfilePage() {
  const { token, user, setUser } = useAuth();
  const headers = { Authorization: `Bearer ${token}` };

  const [form, setForm] = useState(emptyProfile);
  const [smsOptIn, setSmsOptIn] = useState(false);
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [saving, setSaving] = useState(false);
  const [pw, setPw] = useState({ current_password: '', new_password: '', confirm: '' });
  const [savingPw, setSavingPw] = useState(false);

  useEffect(() => {
    if (!user) return;
    setForm({
      first_name: user.first_name || '',
      last_name: user.last_name || '',
      email: user.email || '',
      phone: user.phone || '',
      address_line1: user.address_line1 || '',
      address_line2: user.address_line2 || '',
      state: user.state || '',
      zip: user.zip || '',
    });
    setSmsOptIn(!!user.sms_opt_in);
    setAcceptedTerms(!!user.accepted_terms_at);
  }, [user]);

  const setField = (key, value) => setForm((prev) => ({ ...prev, [key]: value }));

  const handleSaveProfile = async () => {
    if (!form.first_name.trim() || !form.last_name.trim() || !form.email.trim()) {
      toast.error('First name, last name, and email are required');
      return;
    }
    if (smsOptIn && !form.phone.trim()) {
      toast.error('Mobile phone is required to receive SMS notifications');
      return;
    }
    if (smsOptIn && !user?.accepted_terms_at && !acceptedTerms) {
      toast.error('Please accept the Terms and Conditions and Privacy Policy to opt in to SMS');
      return;
    }
    setSaving(true);
    try {
      const res = await axios.patch(
        `${BACKEND_URL}/api/admin/me`,
        {
          ...form,
          sms_opt_in: smsOptIn,
          accepted_terms: acceptedTerms || !!user?.accepted_terms_at,
        },
        { headers }
      );
      if (res.data?.user) setUser(res.data.user);
      if (res.data?.pending_email) {
        toast.success(res.data.message || `Verification sent to ${res.data.pending_email}`);
      } else {
        toast.success(res.data.message || 'Profile updated');
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update profile');
    } finally {
      setSaving(false);
    }
  };

  const handleChangePassword = async () => {
    if (pw.new_password.length < 8) {
      toast.error('New password must be at least 8 characters');
      return;
    }
    if (pw.new_password !== pw.confirm) {
      toast.error('New passwords do not match');
      return;
    }
    setSavingPw(true);
    try {
      await axios.post(
        `${BACKEND_URL}/api/admin/me/password`,
        { current_password: pw.current_password, new_password: pw.new_password },
        { headers },
      );
      setPw({ current_password: '', new_password: '', confirm: '' });
      toast.success('Password updated');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to change password');
    } finally {
      setSavingPw(false);
    }
  };

  return (
    <div className="p-4 sm:p-6 max-w-[900px] mx-auto" data-testid="admin-profile-page">
      <div className="mb-6">
        <h1 className="text-2xl font-['Unbounded'] font-bold text-white flex items-center gap-3">
          <UserCircle className="w-7 h-7 text-blue-400" />
          Profile
        </h1>
        <p className="text-sm font-['Manrope'] text-white/40 mt-1">
          View and update your account information
        </p>
      </div>

      <div className="bg-white/5 border border-white/10 p-6 mb-6">
        <h2 className="text-sm font-['Unbounded'] font-semibold text-white/80 mb-4">Personal information</h2>
        {user?.pending_email && (
          <div className="mb-4 text-sm font-['Manrope'] text-amber-300/90 border border-amber-500/25 bg-amber-500/10 px-3 py-2">
            A verification link was sent to <strong>{user.pending_email}</strong>. Open it once to finish changing your email. Your current login remains {user.email} until then.
          </div>
        )}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className={labelClass}>First Name *</label>
            <input value={form.first_name} onChange={(e) => setField('first_name', e.target.value)} className={inputClass} />
          </div>
          <div>
            <label className={labelClass}>Last Name *</label>
            <input value={form.last_name} onChange={(e) => setField('last_name', e.target.value)} className={inputClass} />
          </div>
          <div>
            <label className={labelClass}>Email *</label>
            <input type="email" value={form.email} onChange={(e) => setField('email', e.target.value)} className={inputClass} />
          </div>
          <div>
            <label className={labelClass}>Phone Number</label>
            <input value={form.phone} onChange={(e) => setField('phone', e.target.value)} className={inputClass} />
          </div>
          <div className="md:col-span-2">
            <label className={labelClass}>Address Line 1</label>
            <input value={form.address_line1} onChange={(e) => setField('address_line1', e.target.value)} className={inputClass} />
          </div>
          <div className="md:col-span-2">
            <label className={labelClass}>Address Line 2</label>
            <input value={form.address_line2} onChange={(e) => setField('address_line2', e.target.value)} className={inputClass} />
          </div>
          <div>
            <label className={labelClass}>State</label>
            <select
              value={form.state}
              onChange={(e) => setField('state', e.target.value)}
              className={`${inputClass} [&>option]:bg-gray-900`}
            >
              <option value="">Select…</option>
              {US_STATES.map((st) => (
                <option key={st} value={st}>{st}</option>
              ))}
            </select>
          </div>
          <div>
            <label className={labelClass}>ZIP</label>
            <input value={form.zip} onChange={(e) => setField('zip', e.target.value)} className={inputClass} />
          </div>
          <div className="md:col-span-2">
            <label className={labelClass}>Position</label>
            <input
              value={user?.role_label || ''}
              readOnly
              className={`${inputClass} text-white/50 cursor-not-allowed`}
            />
            <p className="text-[11px] font-['Manrope'] text-white/35 mt-1.5">
              Position can only be changed by the main admin, and only for other users.
            </p>
          </div>
        </div>
        <div className="flex justify-end mt-6">
          <button
            type="button"
            onClick={handleSaveProfile}
            disabled={saving}
            data-testid="save-profile-btn"
            className="min-h-11 w-full sm:w-auto flex items-center justify-center gap-2 px-5 py-2.5 bg-blue-500 hover:bg-blue-600 text-white text-sm font-['Manrope'] font-semibold disabled:opacity-50"
          >
            <Save className="w-4 h-4" />
            {saving ? 'Saving…' : 'Save Profile'}
          </button>
        </div>
      </div>

      <div className="bg-white/5 border border-white/10 p-6 mb-6" data-testid="staff-sms-profile-section">
        <h2 className="text-sm font-['Unbounded'] font-semibold text-white/80 mb-2">SMS notifications</h2>
        <p className="text-sm font-['Manrope'] text-white/40 mb-4">
          Optional. Use the mobile number in Personal information. You can save your profile without opting in.
        </p>
        <StaffSmsConsentFields
          showPhone={false}
          phone={form.phone}
          onPhoneChange={(value) => setField('phone', value)}
          smsOptIn={smsOptIn}
          onSmsOptInChange={setSmsOptIn}
          acceptedTerms={acceptedTerms}
          onAcceptedTermsChange={setAcceptedTerms}
          termsAlreadyAccepted={!!user?.accepted_terms_at}
        />
        <div className="flex justify-end mt-6">
          <button
            type="button"
            onClick={handleSaveProfile}
            disabled={saving}
            data-testid="save-sms-opt-in-btn"
            className="min-h-11 w-full sm:w-auto flex items-center justify-center gap-2 px-5 py-2.5 bg-blue-500 hover:bg-blue-600 text-white text-sm font-['Manrope'] font-semibold disabled:opacity-50"
          >
            <Save className="w-4 h-4" />
            {saving ? 'Saving…' : 'Save SMS preference'}
          </button>
        </div>
      </div>

      <div className="bg-white/5 border border-white/10 p-6">
        <h2 className="text-sm font-['Unbounded'] font-semibold text-white/80 mb-4 flex items-center gap-2">
          <Lock className="w-4 h-4 text-amber-400" />
          Change password
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className={labelClass}>Current Password</label>
            <input
              type="password"
              value={pw.current_password}
              onChange={(e) => setPw((p) => ({ ...p, current_password: e.target.value }))}
              className={inputClass}
              autoComplete="current-password"
            />
          </div>
          <div>
            <label className={labelClass}>New Password</label>
            <input
              type="password"
              value={pw.new_password}
              onChange={(e) => setPw((p) => ({ ...p, new_password: e.target.value }))}
              className={inputClass}
              autoComplete="new-password"
            />
          </div>
          <div>
            <label className={labelClass}>Confirm New Password</label>
            <input
              type="password"
              value={pw.confirm}
              onChange={(e) => setPw((p) => ({ ...p, confirm: e.target.value }))}
              className={inputClass}
              autoComplete="new-password"
            />
          </div>
        </div>
        <div className="flex justify-end mt-6">
          <button
            type="button"
            onClick={handleChangePassword}
            disabled={savingPw || !pw.current_password || !pw.new_password}
            data-testid="change-password-btn"
            className="min-h-11 w-full sm:w-auto flex items-center justify-center gap-2 px-5 py-2.5 bg-amber-500 hover:bg-amber-600 text-black text-sm font-['Manrope'] font-semibold disabled:opacity-50"
          >
            {savingPw ? 'Updating…' : 'Update Password'}
          </button>
        </div>
      </div>
    </div>
  );
}
